import { useCallback, useMemo, useRef, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { message } from '@surecompanies/core_components'
import {
  completeUpload,
  deleteDocument,
  presignUploadUrl,
} from '../api/plan-override-documents-api'
import { uploadToS3 } from '../utils/uploadToS3'
import { DOCUMENTS_QUERY_KEY } from './useOverrideDocuments'
import { HISTORY_QUERY_KEY } from './useOverrideHistory'
import {
  ALLOWED_MIME_TYPES,
  MAX_SIZE_BY_MIME,
  MAX_FILES_PER_OVERRIDE,
} from '../utils/fileValidation'

export interface StagedUpload {
  tempId: string
  fileName: string
  contentType: string
  fileSize: number
  file: File
}

interface UseEditDocumentStagingOptions {
  ruleId: string
  ruleBase?: string
}

export function useEditDocumentStaging({
  ruleId,
  ruleBase,
}: UseEditDocumentStagingOptions) {
  const queryClient = useQueryClient()
  const [stagedUploads, setStagedUploads] = useState<StagedUpload[]>([])
  const [stagedDeleteIds, setStagedDeleteIds] = useState<Set<string>>(
    () => new Set()
  )
  const [isCommitting, setIsCommitting] = useState(false)

  // Refs keep stageUpload's validation up-to-date across multi-file batch drops
  // without recreating the callback on every state change.
  const stagedUploadsRef = useRef(stagedUploads)
  stagedUploadsRef.current = stagedUploads
  const stagedDeleteIdsRef = useRef(stagedDeleteIds)
  stagedDeleteIdsRef.current = stagedDeleteIds

  const stageUpload = useCallback(
    (file: File, persistedDocCount: number): boolean => {
      if (!ALLOWED_MIME_TYPES.has(file.type)) {
        message.error(
          `${file.name}: unsupported file type. Allowed: PNG, JPEG, WEBP, PDF, DOC, DOCX, XLSX, CSV.`
        )
        return false
      }
      const maxSize = MAX_SIZE_BY_MIME[file.type] ?? 0
      if (file.size > maxSize) {
        const mb = Math.round(maxSize / (1024 * 1024))
        message.error(
          `${file.name}: exceeds the ${mb} MB limit for this file type.`
        )
        return false
      }
      const netCount =
        persistedDocCount -
        stagedDeleteIdsRef.current.size +
        stagedUploadsRef.current.length
      if (netCount >= MAX_FILES_PER_OVERRIDE) {
        message.error(`Maximum ${MAX_FILES_PER_OVERRIDE} files per override.`)
        return false
      }
      const tempId = `staged-${Date.now()}-${file.name}-${Math.random()
        .toString(36)
        .slice(2, 8)}`
      const newEntry: StagedUpload = {
        tempId,
        fileName: file.name,
        contentType: file.type,
        fileSize: file.size,
        file,
      }
      stagedUploadsRef.current = [...stagedUploadsRef.current, newEntry]
      setStagedUploads(stagedUploadsRef.current)
      return true
    },
    []
  )

  const unstageUpload = useCallback((tempId: string) => {
    setStagedUploads((prev) => prev.filter((u) => u.tempId !== tempId))
  }, [])

  const stageDelete = useCallback((docId: string) => {
    setStagedDeleteIds((prev) => {
      if (prev.has(docId)) return prev
      const next = new Set(prev)
      next.add(docId)
      return next
    })
  }, [])

  const unstageDelete = useCallback((docId: string) => {
    setStagedDeleteIds((prev) => {
      if (!prev.has(docId)) return prev
      const next = new Set(prev)
      next.delete(docId)
      return next
    })
  }, [])

  const discard = useCallback(() => {
    setStagedUploads([])
    setStagedDeleteIds(new Set())
  }, [])

  const commit = useCallback(async () => {
    if (stagedUploads.length === 0 && stagedDeleteIds.size === 0) return
    setIsCommitting(true)
    try {
      await Promise.all(
        stagedUploads.map(async (upload) => {
          const { documentId, presignedUploadUrl: uploadUrl } =
            await presignUploadUrl(
              ruleId,
              {
                fileName: upload.fileName,
                contentType: upload.contentType,
                fileSize: upload.fileSize,
              },
              ruleBase
            )
          const result = await uploadToS3({
            presignedUrl: uploadUrl,
            file: upload.file,
            contentType: upload.contentType,
          })
          if (!result.success) {
            throw result.error ?? new Error('S3 upload failed')
          }
          await completeUpload(
            ruleId,
            documentId,
            { sizeBytes: upload.fileSize },
            ruleBase
          )
        })
      )
      await Promise.all(
        Array.from(stagedDeleteIds).map((docId) =>
          deleteDocument(ruleId, docId, ruleBase)
        )
      )
      setStagedUploads([])
      setStagedDeleteIds(new Set())
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: [DOCUMENTS_QUERY_KEY, ruleId, ruleBase],
        }),
        queryClient.invalidateQueries({
          queryKey: [HISTORY_QUERY_KEY, ruleId],
        }),
      ])
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('commit override document changes failed', err)
      message.error('Failed to save document changes. Please try again.')
      throw err
    } finally {
      setIsCommitting(false)
    }
  }, [ruleId, ruleBase, stagedUploads, stagedDeleteIds, queryClient])

  const hasStagedChanges = useMemo(
    () => stagedUploads.length > 0 || stagedDeleteIds.size > 0,
    [stagedUploads.length, stagedDeleteIds.size]
  )

  return {
    stagedUploads,
    stagedDeleteIds,
    stageUpload,
    unstageUpload,
    stageDelete,
    unstageDelete,
    discard,
    commit,
    isCommitting,
    hasStagedChanges,
  }
}
